/**
 * Helpers d'affichage de fuseaux horaires (côté frontend).
 *
 * Contexte :
 *   Un même collaborateur peut être planifié dans plusieurs départements
 *   ayant des fuseaux horaires différents. Pour éviter toute confusion sur
 *   l'heure réelle du shift, on affiche systématiquement le fuseau du
 *   département en face de l'horaire de planification.
 *
 * Formats renvoyés par `formatTimezoneLabel` :
 *   - short : "UTC+1"           (compact — utilisé dans les cartes shift)
 *   - city  : "Paris"           (ville extraite du nom IANA)
 *   - full  : "Paris · UTC+1"   (label lisible pour tooltips / modales)
 *   - name  : "Europe/Paris"    (nom IANA d'origine, tel qu'en BDD)
 *
 * Si `tzName` est vide/invalide, on renvoie des chaînes vides pour éviter
 * de forcer un fallback trompeur côté UI (le composant appelant décide).
 */

const _offsetCache = new Map();

/** Retourne juste l'offset UTC actuel (ex "UTC+1", "UTC-5:30") pour un fuseau IANA. */
function _resolveOffset(tzName) {
  if (!tzName) return "";
  if (_offsetCache.has(tzName)) return _offsetCache.get(tzName);
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tzName,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === "timeZoneName")?.value || "";
    // Ex. "GMT+1" ou "GMT" → on normalise en "UTC+1" / "UTC"
    const norm = raw.replace(/^GMT/, "UTC").replace(/^UTC$/, "UTC±0");
    _offsetCache.set(tzName, norm);
    return norm;
  } catch {
    _offsetCache.set(tzName, "");
    return "";
  }
}

/** Extrait la partie ville du nom IANA (ex. "Europe/Paris" → "Paris"). */
function _extractCity(tzName) {
  if (!tzName || typeof tzName !== "string") return "";
  const seg = tzName.split("/").pop() || "";
  return seg.replace(/_/g, " ");
}

export function formatTimezoneLabel(tzName) {
  const name = tzName || "";
  const city = _extractCity(name);
  const short = _resolveOffset(name);
  const full = city && short ? `${city} · ${short}` : city || short || "";
  return { name, city, short, full };
}

/** Version raccourcie pour affichage inline dans les listes/cartes. */
export function timezoneShort(tzName) {
  return formatTimezoneLabel(tzName).short;
}

/** Version longue pour tooltips / modales de détail. */
export function timezoneFull(tzName) {
  const l = formatTimezoneLabel(tzName);
  if (!l.full) return "";
  return l.name ? `${l.full} (${l.name})` : l.full;
}
